// src/app/services/consultas/consultas-orden.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/* ========== DTOs / TIPOS ========== */

// Respuestas con envoltorio { response: ... }
interface FindAllTiposResp {
    response: TipoOrdenConsulta[];
}
interface OneTipoResp {
    response: TipoOrdenConsulta;
}

export interface TipoOrdenVentana {
    days: number[]; // 0..6 (0=Dom)
    start: string; // "HH:mm"
    end: string; // "HH:mm"
    overnight?: boolean; // backend lo infiere si cruza medianoche
}

export interface TipoOrdenConsulta {
    _id?: string;
    nombre: string; // p.ej. "Guardia Nocturna"
    descripcion?: string;
    sedes: string[];
    activo: boolean;
    ventanas: TipoOrdenVentana[];
    maxPerMonth?: number;
    maxTotal?: number;
    caducidadHoras?: number;
    createdAt?: string;
    updatedAt?: string;
    __v?: number;
}

// POST /signo-orden-consultas/validar
export interface ValidarGeneracionRequestDto {
    tipo: string; // ObjectId de tipo de orden
    usuario: string; // ObjectId de usuario
    when: string; // ISO string
    groupBy?: string; // ej: "estadoTipo"
}

export interface ValidarGeneracionResponseDto {
    puede: boolean;
    horarioHabilitado: boolean;
    motivos: string[];
    fecha: string; // ISO
    conteos?: { hoy?: number; mes?: number; ventana?: number };
    limites?: {
        maxPerDay?: number;
        maxPerMonth?: number;
        maxPerWindow?: number;
    };
    ventanaActual?: { start: string; end: string };
}

// POST /signo-orden-consultas/generar
export interface GenerarOrdenRequestDto {
    tipo: string; // ObjectId
    usuario: string; // ObjectId
}

export interface GenerarOrdenResponseDto {
    fecha: string; // ISO
    numeroBeneficiario: string;
    numeroOrden: string;
    nombreApellido: string;
    edad: number;
    plan: string;
    sede: string;
}

/* ========== Helpers para la UI ========== */

export interface ValidacionResultado {
    ok: boolean; // true => se puede generar
    puede: boolean; // espejo del backend
    message: string; // mensaje listo para el usuario
    raw?: ValidarGeneracionResponseDto; // respuesta completa
}

export interface GeneracionResultado {
    ok: boolean; // true => generó
    message: string; // mensaje listo para el usuario
    data?: GenerarOrdenResponseDto; // datos generados
}

/* ========== Servicio ========== */

@Injectable({ providedIn: 'root' })
export class ConsultasOrdenService {
    private readonly BASE = environment.urlBackend;

    constructor(private http: HttpClient) {}

    /* ------------ headers ------------- */
    private buildHeaders(): HttpHeaders {
        const token = localStorage.getItem('signo-token') || '';
        let headers = new HttpHeaders()
            .set('Accept', 'application/json')
            .set('Content-Type', 'application/json');
        if (token) headers = headers.set('x-token', token);
        return headers;
    }

    /* ------------ extracción de mensaje de error del backend ------------- */
    private extractBackendMessage(err: any): string {
        const e = err?.error || err;
        const msg =
            e?.message ||
            e?.error?.message ||
            err?.message ||
            'Error inesperado. Intente nuevamente.';
        return typeof msg === 'string' ? msg : JSON.stringify(msg);
    }

    private handleError = (err: any) =>
        throwError(new Error(this.extractBackendMessage(err)));

    /* ========== TIPOS DE ORDEN: LISTAR / CREAR / EDITAR ========== */

    // Cache simple para listado
    private _tiposCache$?: Observable<TipoOrdenConsulta[]>;

    /** GET /signo-orden-consulta-tipos/findAll */
    getTiposOrden$(): Observable<TipoOrdenConsulta[]> {
        if (!this._tiposCache$) {
            this._tiposCache$ = this.http
                .get<FindAllTiposResp>(
                    `${this.BASE}/signo-orden-consulta-tipos/findAll`,
                    { headers: this.buildHeaders() }
                )
                .pipe(
                    map((r) => r.response || []),
                    shareReplay(1),
                    catchError(this.handleError)
                );
        }
        return this._tiposCache$;
    }

    /** Fuerza refresco del cache del listado */
    refreshTiposOrden$(): Observable<TipoOrdenConsulta[]> {
        this._tiposCache$ = undefined;
        return this.getTiposOrden$();
    }

    /** POST /signo-orden-consulta-tipos (alta) */
    createTipoOrden$(
        payload: Omit<
            TipoOrdenConsulta,
            '_id' | '__v' | 'createdAt' | 'updatedAt'
        >
    ): Observable<TipoOrdenConsulta> {
        return this.http
            .post<OneTipoResp>(
                `${this.BASE}/signo-orden-consulta-tipos`,
                payload,
                { headers: this.buildHeaders() }
            )
            .pipe(
                map((r) => r.response),
                catchError(this.handleError)
            );
    }

    /** PUT /signo-orden-consulta-tipos/:id (modificación) */
    updateTipoOrden$(
        id: string,
        payload: Partial<TipoOrdenConsulta>
    ): Observable<TipoOrdenConsulta> {
        return this.http
            .put<OneTipoResp>(
                `${this.BASE}/signo-orden-consulta-tipos/updateOne/${id}`,
                payload,
                { headers: this.buildHeaders() }
            )
            .pipe(
                map((r) => r.response),
                catchError(this.handleError)
            );
    }

    /* ========== ENDPOINTS ORDEN (VALIDAR / GENERAR) ========== */

    /** POST /signo-orden-consultas/validar  */
    validarGeneracion(
        dto: ValidarGeneracionRequestDto
    ): Observable<ValidarGeneracionResponseDto> {
        return this.http
            .post<any>(`${this.BASE}/signo-orden-consultas/validar`, dto, {
                headers: this.buildHeaders(),
            })
            .pipe(
                map(
                    (res) =>
                        (res?.response ?? res) as ValidarGeneracionResponseDto
                ),
                catchError(this.handleError)
            );
    }

    /** POST /signo-orden-consultas/generar */
    generarOrden(
        dto: GenerarOrdenRequestDto
    ): Observable<GenerarOrdenResponseDto> {
        return this.http
            .post<any>(`${this.BASE}/signo-orden-consultas/generar`, dto, {
                headers: this.buildHeaders(),
            })
            .pipe(
                map(
                    (res) =>
                        (res?.response ??
                            res) as ValidarGeneracionResponseDto as any
                ),
                // el backend que mostraste devuelve { response: {...} }, casteamos a tu DTO final
                map((r: any) => r as GenerarOrdenResponseDto),
                catchError(this.handleError)
            );
    }

    /* ========== AZÚCAR PARA LA VISTA (mensajes listos) ========== */

    /** Interpreta la validación y arma un mensaje claro para el afiliado */
    private buildValidacionMessage(r: ValidarGeneracionResponseDto): string {
        if (r?.motivos?.length) return r.motivos.join(' · ');

        if (r && !r.horarioHabilitado) {
            return 'No es un horario habilitado para generar órdenes de consulta.';
        }

        const hoy = r?.conteos?.hoy ?? null;
        const mes = r?.conteos?.mes ?? null;
        const ventana = r?.conteos?.ventana ?? null;

        const maxPerDay = r?.limites?.maxPerDay ?? null;
        const maxPerMonth = r?.limites?.maxPerMonth ?? null;
        const maxPerWindow = r?.limites?.maxPerWindow ?? null;

        if (maxPerDay != null && hoy != null && hoy >= maxPerDay) {
            return 'Se alcanzó el máximo diario de órdenes. Intente mañana.';
        }
        if (maxPerMonth != null && mes != null && mes >= maxPerMonth) {
            return 'Se alcanzó el máximo mensual de órdenes para su cuenta.';
        }
        if (
            maxPerWindow != null &&
            ventana != null &&
            ventana >= maxPerWindow
        ) {
            return 'Se alcanzó el máximo permitido en la ventana actual.';
        }

        return 'No es posible generar la orden en este momento.';
    }

    /** Versión “lista para UI” de la validación (Observable) */
    validarConMensaje(
        dto: ValidarGeneracionRequestDto
    ): Observable<ValidacionResultado> {
        return this.validarGeneracion(dto).pipe(
            map((r) => {
                const puede = !!r.puede;
                return {
                    ok: puede,
                    puede,
                    message: puede
                        ? 'Validación OK. Podés generar la orden.'
                        : this.buildValidacionMessage(r),
                    raw: r,
                } as ValidacionResultado;
            }),
            catchError((err) =>
                throwError(new Error(this.extractBackendMessage(err)))
            )
        );
    }

    /** Versión “lista para UI” de la generación (Observable) */
    generarConMensaje(
        dto: GenerarOrdenRequestDto
    ): Observable<GeneracionResultado> {
        return this.generarOrden(dto).pipe(
            map(
                (data) =>
                    ({
                        ok: true,
                        message: 'Orden generada correctamente.',
                        data,
                    } as GeneracionResultado)
            ),
            catchError((err) =>
                throwError(new Error(this.extractBackendMessage(err)))
            )
        );
    }

    /* ========== PROMISE WRAPPERS (compat) ========== */

    async validarConMensajePromise(
        dto: ValidarGeneracionRequestDto
    ): Promise<ValidacionResultado> {
        try {
            const r = await this.validarGeneracion(dto).toPromise();
            const puede = !!r.puede;
            return {
                ok: puede,
                puede,
                message: puede
                    ? 'Validación OK. Podés generar la orden.'
                    : this.buildValidacionMessage(r),
                raw: r,
            };
        } catch (err) {
            throw new Error(this.extractBackendMessage(err));
        }
    }

    async generarConMensajePromise(
        dto: GenerarOrdenRequestDto
    ): Promise<GeneracionResultado> {
        try {
            const data = await this.generarOrden(dto).toPromise();
            return { ok: true, message: 'Orden generada correctamente.', data };
        } catch (err) {
            throw new Error(this.extractBackendMessage(err));
        }
    }

    async listarPorPeriodo(
        fromIso?: string,
        toIso?: string
    ): Promise<TipoOrdenConsulta[]> {
        var headers = this.buildHeaders();

        var params = new HttpParams();
        if (fromIso) params = params.set('from', fromIso);
        if (toIso) params = params.set('to', toIso);

        return this.http
            .get<any>(`${this.BASE}/signo-orden-consultas/por-periodo`, {
                headers,
                params,
            })
            .toPromise()
            .then(function (resp) {
                // Si la API devuelve { response: { response: [...] } }
                if (resp && resp.response && resp.response.response) {
                    return resp.response.response;
                }
                // fallback
                if (resp && resp.response) {
                    return resp.response;
                }
                return [];
            })

            .catch(this.handleError);
    }
}
